import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  RICERCA_LISTINI,
  type RigaListino,
  type Validita,
  esportaListini,
  useConteggiValidita,
  useDefinizioneFiltriListini,
  useListini,
  useOpzioniListini,
} from '../api/listini.js';
import { AzioniDiMassa } from '../componenti/AzioniDiMassa.js';
import { BarraFiltri } from '../componenti/BarraFiltri.js';
import { PiedeElenco } from '../componenti/PiedeElenco.js';
import { type Colonna, Tabella } from '../componenti/Tabella.js';
import { giorno } from '../formati.js';
import { useRicerca } from '../useRicerca.js';
import { useSelezione } from '../useSelezione.js';
import { ModaleListino } from './ModaleListino.js';
import { locale, t } from '../i18n/lingua.js';

/**
 * L'elenco dei listini.
 *
 * Ventiquattro clienti hanno più di un listino, e tutti hanno una finestra di
 * date: la domanda vera non è «quali listini esistono» ma **quale vale oggi**.
 * Nel v2 bisogna aprirli e confrontare le date a mano; qui la validità è una
 * colonna, un filtro e tre numeri sopra l'elenco.
 */

export const VALIDITA: Record<Validita, { testo: string; classe: string }> = {
  active: { testo: t('in vigore'), classe: 'badge-ok' },
  future: { testo: t('dal futuro'), classe: 'badge-warn' },
  expired: { testo: t('scaduto'), classe: '' },
};

const COLONNE: Colonna<RigaListino>[] = [
  {
    chiave: 'name',
    titolo: t('Listino'),
    ordinabile: true,
    larghezzaMax: 320,
    cella: (r) => (
      <>
        <Link className="cell-link strong" to="/price-lists/$id" params={{ id: String(r.id) }}>
          {r.name}
        </Link>
        {r.archived && (
          <span className="badge" style={{ marginLeft: 8 }}>
            {t('archiviato')}
          </span>
        )}
      </>
    ),
  },
  {
    chiave: 'customer',
    titolo: t('Cliente'),
    ordinabile: true,
    larghezzaMax: 260,
    cella: (r) =>
      r.customerId ? (
        <Link className="cell-link" to="/customers/$id" params={{ id: String(r.customerId) }}>
          {r.customer}
        </Link>
      ) : (
        // nei dati travasati non succede mai, ma un listino può valere per un
        // intero paese invece che per un cliente
        (r.country ?? <span className="dim">—</span>)
      ),
  },
  {
    chiave: 'validity',
    titolo: t('Validità'),
    cella: (r) => {
      const v = VALIDITA[r.validity];
      return <span className={`badge ${v.classe}`}>{v.testo}</span>;
    },
  },
  {
    chiave: 'startDate',
    titolo: t('Dal'),
    ordinabile: true,
    monospazio: true,
    cella: (r) => giorno(r.startDate),
  },
  {
    chiave: 'endDate',
    titolo: 'Al',
    ordinabile: true,
    monospazio: true,
    cella: (r) => giorno(r.endDate),
  },
  {
    chiave: 'shippingMethod',
    titolo: t('Resa'),
    ordinabile: true,
    cella: (r) => <span className="badge">{r.shippingMethod.toUpperCase()}</span>,
  },
  {
    chiave: 'flatDiscountPercentage',
    titolo: t('Sconto'),
    allineamento: 'destra',
    monospazio: true,
    // uno sconto dello zero per cento è come non averlo: mostrarlo confonde
    cella: (r) =>
      r.flatDiscountPercentage && Number(r.flatDiscountPercentage) !== 0 ? (
        `${Number(r.flatDiscountPercentage).toLocaleString('it-IT')}%`
      ) : (
        <span className="dim">—</span>
      ),
  },
  {
    chiave: 'lines',
    titolo: t('Righe'),
    allineamento: 'destra',
    monospazio: true,
    cella: (r) => r.lines.toLocaleString('it-IT'),
  },
];

export function Listini() {
  const { ricerca, testo, setTesto, cambia, vaiA, ordinaPer, filtra } =
    useRicerca(RICERCA_LISTINI);
  const elenco = useListini(ricerca);
  const [selezione, setSelezione] = useSelezione(elenco.data?.rows);
  const conteggi = useConteggiValidita(ricerca);
  const definizioni = useDefinizioneFiltriListini();
  const opzioni = useOpzioniListini();
  const navigate = useNavigate();
  const [nuovoAperto, setNuovoAperto] = useState(false);

  const dati = elenco.data;
  const scelta = ricerca.filters?.validity;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Listini')}</h1>
          <div className="page-sub">
            {dati
              ? ricerca.archived
                ? t('{n} listini, archiviati compresi', { n: dati.total.toLocaleString(locale()) })
                : t('{n} listini', { n: dati.total.toLocaleString(locale()) })
              : elenco.error
                ? t('Elenco non disponibile')
                : t('Un momento…')}
          </div>
        </div>
        <div className="page-actions">
          <button
            className="btn"
            // con un filtro sbagliato l'elenco dice già perché: il file aprirebbe il JSON dell'errore
            disabled={Boolean(elenco.error)}
            onClick={() => esportaListini(ricerca, 'csv')}>
            CSV
          </button>
          <button
            className="btn"
            // con un filtro sbagliato l'elenco dice già perché: il file aprirebbe il JSON dell'errore
            disabled={Boolean(elenco.error)}
            onClick={() => esportaListini(ricerca, 'xlsx')}>
            Excel
          </button>
          <button className="btn btn-primary" onClick={() => setNuovoAperto(true)}>
            {t('Nuovo listino')}
          </button>
        </div>
      </div>

      {/**
       * Due strade per fare un listino: crearlo da zero, oppure aprire quello
       * del periodo prima e duplicarlo. La seconda è quasi sempre quella
       * giusta — il listino Campbell ha 58 righe — e per questo la riga qui
       * sotto la ricorda accanto ai numeri.
       */}
      <div className="statebar">
        <button
          className={scelta === undefined ? 'state-tab state-tab-on' : 'state-tab'}
          onClick={() => filtra('validity', undefined)}
        >
          {t('Tutti')}
          {/* la somma delle linguette: il totale dell'elenco, con una validità
              scelta, era il numero di quella */}
          <b>
            {Object.values(conteggi.data?.counts ?? {})
              .reduce((s, n) => s + n, 0)
              .toLocaleString('it-IT')}
          </b>
        </button>
        {(['active', 'future', 'expired'] as Validita[]).map((v) => (
          <button
            key={v}
            className={scelta === v ? 'state-tab state-tab-on' : 'state-tab'}
            onClick={() => filtra('validity', scelta === v ? undefined : v)}
          >
            {VALIDITA[v].testo}
            <b>{(conteggi.data?.counts[v] ?? 0).toLocaleString('it-IT')}</b>
          </button>
        ))}
        <span className="hub-nota" style={{ border: 'none', background: 'none', alignSelf: 'center' }}>
          {t('Se il listino esiste già per il periodo prima, aprilo e usa Duplica: si porta dietro tutti i prezzi.')}
        </span>
      </div>

      <div className="filterbar">
        <span className="omnisearch" style={{ maxWidth: 340, flex: '0 1 340px' }}>
          <span>⌕</span>
          <input
            className="input"
            style={{ border: 'none', height: 22, padding: 0, background: 'transparent' }}
            placeholder={t('Cerca per nome del listino o cliente')}
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
          />
        </span>

        <button
          className={ricerca.archived ? 'chip chip-set' : 'chip'}
          onClick={() => cambia({ archived: !ricerca.archived })}
        >
          {ricerca.archived ? t('Con gli archiviati') : 'Mostra archiviati'}
        </button>

        <BarraFiltri
          definizioni={definizioni.data?.filters ?? []}
          opzioni={definizioni.data?.options ?? {}}
          valori={ricerca.filters}
          onCambia={filtra}
        />
      </div>

      <AzioniDiMassa
        percorso="/api/price-lists/bulk"
        nome="listini"
        selezione={selezione}
        conElimina
        conRipristina={ricerca.archived}
        chiaviDaRileggere={['listini', 'listini-conteggi']}
        onFatto={() => setSelezione(new Set())}
        onAnnulla={() => setSelezione(new Set())}
      />

      <div className="table-wrap" style={{ marginTop: 12, marginBottom: 24 }}>
        <Tabella
          colonne={COLONNE}
          righe={dati?.rows ?? []}
          chiaveRiga={(r) => r.id}
          ordina={ricerca.sort}
          verso={ricerca.dir}
          onOrdina={ordinaPer}
          selezione={selezione}
          onSelezione={setSelezione}
          caricamento={elenco.isFetching}
          errore={elenco.error}
          vuoto={
            ricerca.q ? t('Nessun listino per «{cerca}».', { cerca: ricerca.q }) : t('Nessun listino con questi filtri.')
          }
        />
        <PiedeElenco
          pagina={dati?.page ?? 1}
          pagine={dati?.pages ?? 1}
          totale={dati?.total ?? 0}
          dimensione={ricerca.size}
          nomePlurale={t('listini')}
          onPagina={vaiA}
          onDimensione={(size) => cambia({ size })}
        />
      </div>

      {nuovoAperto && (
        <ModaleListino
          listino={null}
          opzioni={opzioni.data}
          onChiudi={() => setNuovoAperto(false)}
          onCreato={(id) => {
            setNuovoAperto(false);
            // si entra subito nel listino appena creato: il passo successivo è
            // aggiungerci gli articoli, e sta lì
            void navigate({ to: '/price-lists/$id', params: { id: String(id) } });
          }}
        />
      )}
    </>
  );
}
